import type { ApiGroup, Endpoint } from "../types";

import { ERROR_CODES, SANDBOX_API_BASE } from "../constants";

interface PostmanCredential {
  clientId: string;
  clientSecret?: string;
}

interface PostmanRequestUrl {
  host: string[];
  path: string[];
  query?: Array<{
    key: string;
    value: string;
    description?: string;
    disabled?: boolean;
  }>;
  raw: string;
  variable?: Array<{ key: string; value: string; description?: string }>;
}

interface PostmanRequest {
  body?: { mode: "raw"; raw: string; options?: { raw: { language: "json" } } };
  description?: string;
  header: Array<{
    key: string;
    value: string;
    type: "text";
    description?: string;
    disabled?: boolean;
  }>;
  method: string;
  url: PostmanRequestUrl;
}

interface PostmanEvent {
  listen: "test";
  script: { type: "text/javascript"; exec: string[] };
}

interface PostmanItem {
  event?: PostmanEvent[];
  name: string;
  request: PostmanRequest;
  response: Array<{ name: string; status: string; code: number; body: string }>;
}

interface PostmanFolder {
  description?: string;
  item: PostmanItem[];
  name: string;
}

const buildUrl = (endpoint: Endpoint): PostmanRequestUrl => {
  // Keep `:param` segments as-is so Postman resolves them from `variable`.
  const path = endpoint.path.replace(/^\//, "").split("/");
  const variable = (endpoint.pathParams ?? []).map(p => ({
    key: p.name,
    value: p.example ?? "",
    description: p.description,
  }));
  // Mirror the Explorer, which only sends query params that have a value.
  const query = (endpoint.queryParams ?? []).map(p => ({
    key: p.name,
    value: p.example ?? "",
    description: p.description,
    ...(p.example ? {} : { disabled: true }),
  }));
  const activeQuery = query.filter(q => !q.disabled);

  return {
    raw: `{{baseUrl}}${endpoint.path}${
      activeQuery.length > 0
        ? `?${activeQuery.map(q => `${q.key}=${q.value}`).join("&")}`
        : ""
    }`,
    host: ["{{baseUrl}}"],
    path,
    ...(query.length > 0 ? { query } : {}),
    ...(variable.length > 0 ? { variable } : {}),
  };
};

// Spec-specific headers (e.g. client-id). Like the Explorer, which only sends
// headers that have a value, ones without an example start disabled.
const buildCustomHeaders = (endpoint: Endpoint): PostmanRequest["header"] =>
  (endpoint.headerParams ?? []).map(h => ({
    key: h.name,
    value: h.example ?? "",
    type: "text" as const,
    description: h.required ? `Required. ${h.description}` : h.description,
    ...(h.example ? {} : { disabled: true }),
  }));

const buildRequest = (endpoint: Endpoint): PostmanRequest => ({
  method: endpoint.method,
  header: [
    { key: "Authorization", value: "Bearer {{bearer_token}}", type: "text" },
    { key: "Content-Type", value: "application/json", type: "text" },
    { key: "Accept", value: "application/json", type: "text" },
    ...buildCustomHeaders(endpoint),
  ],
  ...(endpoint.sampleBody
    ? {
        body: {
          mode: "raw" as const,
          raw: JSON.stringify(endpoint.sampleBody, null, 2),
          options: { raw: { language: "json" as const } },
        },
      }
    : {}),
  url: buildUrl(endpoint),
  description: endpoint.description,
});

// Mirrors the bodies the sandbox mock server returns (see
// sandbox/src/mock_server.py and middleware/rate_limiter.py). Messages are
// representative.
const buildErrorBody = (code: number, endpoint: Endpoint): unknown => {
  switch (code) {
    case 400:
      return { error: "Bad Request", message: "Invalid JSON: Expecting value" };
    case 401:
      return { error: "Unauthorized", message: "Authentication required" };
    case 403:
      return {
        error: "Forbidden",
        message: "Only clients can access this resource",
      };
    case 404:
      return {
        error: "Not Found",
        message: `Path ${endpoint.path} not found`,
      };
    case 422:
      return {
        error: "Validation Error",
        message: "Request validation failed with 1 error(s)",
        errors: [{ detail: "'field' is a required property", pointer: "" }],
      };
    case 429:
      return {
        title: "Too Many Requests",
        description: "Rate limit exceeded. Try again in 30 seconds.",
      };
    default:
      return {
        error: "Internal Server Error",
        message: "An unexpected error occurred",
      };
  }
};

const SUCCESS_REASONS: Record<number, string> = {
  200: "OK",
  201: "Created",
  202: "Accepted",
  204: "No Content",
};

const buildSuccessResponse = (endpoint: Endpoint) => {
  // Older catalogs don't send sampleStatus; fall back to the previous guess.
  const code =
    endpoint.sampleStatus ?? (endpoint.method === "POST" ? 201 : 200);
  return {
    name: "Example response",
    status: SUCCESS_REASONS[code] ?? "Success",
    code,
    // 204 responses carry no body.
    body:
      code === 204 || endpoint.sampleResponse === undefined
        ? ""
        : JSON.stringify(endpoint.sampleResponse, null, 2),
  };
};

const buildItem = (endpoint: Endpoint): PostmanItem => ({
  name: endpoint.label,
  request: buildRequest(endpoint),
  response: [
    buildSuccessResponse(endpoint),
    ...ERROR_CODES.map(err => ({
      name: err.reason,
      status: err.reason,
      code: err.code,
      body: JSON.stringify(buildErrorBody(err.code, endpoint), null, 2),
    })),
  ],
});

const buildOAuthFolder = (): PostmanFolder => ({
  name: "Authentication",
  description:
    "Exchange your client credentials for a bearer token before calling other endpoints.",
  item: [
    {
      name: "Get bearer token",
      event: [
        {
          listen: "test" as const,
          script: {
            type: "text/javascript" as const,
            exec: [
              "const data = pm.response.json();",
              'if (data.access_token) { pm.collectionVariables.set("bearer_token", data.access_token); }',
            ],
          },
        },
      ],
      request: {
        method: "POST",
        header: [
          { key: "Content-Type", value: "application/json", type: "text" },
          { key: "Accept", value: "application/json", type: "text" },
        ],
        body: {
          mode: "raw",
          raw: JSON.stringify(
            {
              grant_type: "client_" + "credentials",
              client_id: "{{client" + "_id}}",
              client_secret: "{{client" + "_secret}}",
            },
            null,
            2,
          ),
          options: { raw: { language: "json" } },
        },
        url: {
          raw: "{{baseUrl}}/auth/token",
          host: ["{{baseUrl}}"],
          path: ["auth", "token"],
        },
        description:
          "Returns a short-lived bearer token. Save the access_token to the bearer_token environment variable for subsequent requests.",
      },
      response: [
        {
          name: "Success",
          status: "OK",
          code: 200,
          body: JSON.stringify(
            {
              access_token: "mock_" + "token_value",
              token_type: "Bearer",
              expires_in: 3600,
            },
            null,
            2,
          ),
        },
      ],
    },
  ],
});

export const buildPostmanCollection = (
  groups: ApiGroup[],
  credential?: PostmanCredential,
) => {
  const collection = {
    info: {
      _postman_id: `thredd-sandbox-${Date.now()}`,
      name: "Thredd Sandbox API",
      description:
        "Generated from the Thredd Sandbox. Drop your OAuth credentials into the collection variables and start exploring.",
      schema:
        "https://schema.getpostman.com/json/collection/v2.1.0/collection.json",
    },
    item: [
      buildOAuthFolder(),
      ...groups.map(group => ({
        name: group.label,
        description: group.description,
        item: group.endpoints.map(buildItem),
      })),
    ],
    variable: [
      { key: "baseUrl", value: SANDBOX_API_BASE, type: "string" },
      { key: "client_id", value: credential?.clientId ?? "", type: "string" },
      {
        key: "client_secret",
        value: credential?.clientSecret ?? "",
        type: "string",
      },
      { key: "bearer_token", value: "", type: "string" },
    ],
  };
  return collection;
};

export const downloadPostmanCollection = (
  groups: ApiGroup[],
  credential?: PostmanCredential,
) => {
  const collection = buildPostmanCollection(groups, credential);
  const blob = new Blob([JSON.stringify(collection, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "thredd-sandbox.postman_collection.json";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
